import AutosuggestWorker from "client/api/autosuggest.worker";
import {
  GlobalContext,
  GlobalContextProvider,
} from "client/context/GlobalContext";
import { useEventBus } from "client/hooks/useEventBus";
import { useKeyboardToClose } from "client/hooks/useKeyboardToClose";
import { useTrackingDataLayer } from "client/hooks/useTrackingDataLayer";
import useWorker from "client/hooks/useWorker";
import {
  getCachedRecommendations,
  setCachedRecommendations,
} from "client/utils/storage";
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ASResponse, SearchResponse } from "types/searchApi/response";

const isLocal = process.env.NODE_ENV === "development";

const AutosuggestModal = React.lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "client/components/modal/autosuggest-modal"
    ),
);

const getSearchAPIBaseURL = () =>
  window.__INITIAL_STATE_autosuggest_search_bar__.SEARCH_API_BASE_URL;

const Autosuggest = (): JSX.Element => {
  const [showModal, setShowModal] = useState(false);
  const [initData, setInitData] = useState<SearchResponse | ASResponse | null>(
    null,
  );

  const [getWorkerResults] = useWorker(() => new AutosuggestWorker());

  const openModal = useCallback((): void => {
    setShowModal(true);
  }, []);

  const closeModal = useCallback((): void => {
    setShowModal(false);
  }, []);

  const getResults = useCallback<GlobalContext["getResults"]>(
    async (searchTerm?: string) => {
      const res = await getWorkerResults({
        searchTerm,
        env: { SEARCH_API_BASE_URL: getSearchAPIBaseURL() },
      });

      return res?.data ?? null;
    },
    [getWorkerResults],
  );

  useEventBus(openModal);

  useEffect(() => {
    (async () => {
      const cachedRex = getCachedRecommendations();
      if (cachedRex) return setInitData(cachedRex);

      const res = await getWorkerResults<ASResponse>({
        type: "recommendations",
        env: { SEARCH_API_BASE_URL: getSearchAPIBaseURL() },
      });
      if (res.data) setCachedRecommendations(res.data);

      setInitData(res.data);
    })();
  }, [getWorkerResults]);

  useKeyboardToClose(closeModal);

  useTrackingDataLayer();

  const globalCtxVal = useMemo(
    () => ({ closeModal, getResults, initData }),
    [closeModal, getResults, initData],
  );

  return (
    <>
      <GlobalContextProvider value={globalCtxVal}>
        {showModal && (
          <Suspense fallback={null}>
            <AutosuggestModal />
          </Suspense>
        )}
      </GlobalContextProvider>
      {isLocal && <button onClick={openModal}>Open Search</button>}
    </>
  );
};

export default Autosuggest;
